import Vue from 'vue';
import type { EmbeddedFormFileViewerManifest, FileViewerFile } from '@cognitoforms/file-viewer/src/file-viewer.types';
import { buildFileViewerManifest as buildFileViewerManifestFromHosts } from './file-viewer-manifest';
import type { FileViewerHost } from './file-viewer-manifest';
import type { FormSession } from 'src/web-api/form-session';
import type Log from 'src/framework/logging/log';
import { importWithRetry } from 'src/util/retryable-import';
import type FileViewerLightbox from './FileViewerLightbox.vue';
import type FileViewerDock from './FileViewerDock.vue';

type FileViewerHostComponent = typeof FileViewerLightbox | typeof FileViewerDock;

function emptyFileViewerManifest(): EmbeddedFormFileViewerManifest {
	return { formTitle: '', entryTitle: '', fields: [], files: [] };
}

export default class FormFileViewer {
	constructor(private readonly form: Vue & { session: FormSession; log: Log; canHostLocalFileViewer: boolean; buildFileViewerManifest(options?: { includeHidden?: boolean }): Promise<EmbeddedFormFileViewerManifest> }) {}

	destroy() {
		this.unmountFileViewerDock();
		this.unmountFileViewerLightbox();
	}

	private fileViewerHostsById = new Map<string, FileViewerHost>();

	registerUpload(id: string, host: FileViewerHost) {
		if (!this.form.canHostLocalFileViewer)
			return;

		this.fileViewerHostsById.set(id, host);
		this.queueFileViewerDockRefresh();
	}

	unregisterUpload(id: string) {
		if (this.fileViewerHostsById.delete(id))
			this.queueFileViewerDockRefresh();
	}

	private queueFileViewerDockRefresh(): void {
		if (this.fileViewerDockRefreshQueued)
			return;

		this.fileViewerDockRefreshQueued = true;
		this.form.$nextTick(() => {
			this.fileViewerDockRefreshQueued = false;
			void this.refreshFileViewerDock();
		});
	}

	async buildFileViewerManifest(options?: { includeHidden?: boolean }): Promise<EmbeddedFormFileViewerManifest> {
		return buildFileViewerManifestFromHosts(Array.from(this.fileViewerHostsById.values()), options);
	}

	private fileViewerLightboxComponent: (Vue & { visible: boolean; manifest: EmbeddedFormFileViewerManifest; selectedFileId: string }) | null = null;
	private fileViewerCurrentFileId: string | null = null;
	private fileViewerLightboxMountPromise: Promise<void> | null = null;
	private fileViewerLightboxMountGeneration = 0;
	private fileViewerOpenToken = 0;

	private fileViewerDockComponent: (Vue & { manifest: EmbeddedFormFileViewerManifest }) | null = null;
	private fileViewerDockCurrentFileId: string | null = null;
	private fileViewerDockIncludeHidden = false;
	private fileViewerDockRefreshToken = 0;
	private fileViewerDockRefreshQueued = false;
	private fileViewerDockMountGeneration = 0;

	private createFileViewerHost<P extends Record<string, unknown>>(component: FileViewerHostComponent, props: P, listeners: Record<string, (...args: any[]) => void>): Vue & P {
		return new Vue({
			parent: this.form,
			data: () => ({ ...props }),
			render(h) {
				return h(component as unknown as Parameters<typeof Vue.extend>[0] | typeof Vue, { props: { ...this.$data }, on: listeners });
			}
		}) as Vue & P;
	}

	ensureFileViewerMounted(): Promise<void> {
		if (this.fileViewerLightboxComponent || !this.form.canHostLocalFileViewer)
			return Promise.resolve();

		if (!this.fileViewerLightboxMountPromise) {
			this.fileViewerLightboxMountPromise = this.mountFileViewerLightbox(this.fileViewerLightboxMountGeneration).catch(e => {
				this.fileViewerLightboxMountPromise = null;
				throw e;
			});
		}

		return this.fileViewerLightboxMountPromise;
	}

	private async mountFileViewerLightbox(generation: number): Promise<void> {
		const FileViewerLightbox = (await importWithRetry(() => import(/* webpackChunkName: "FileViewerLightbox" */ './FileViewerLightbox.vue'))).default;

		if (generation !== this.fileViewerLightboxMountGeneration)
			return;

		const component = this.createFileViewerHost(FileViewerLightbox, {
			manifest: emptyFileViewerManifest(),
			staticLibrariesUrl: this.form.session.staticLibrariesUrl || '',
			closable: true,
			visible: false,
			formEl: this.form.$el,
			selectedFileId: this.fileViewerCurrentFileId || ''
		}, {
			close: () => this.closeFileViewer(),
			select: (file: FileViewerFile) => this.selectFileViewerFile(file.fileId)
		});

		component.$mount();

		this.fileViewerLightboxComponent = component;
	}

	/**
	 * Opens the local file viewer lightbox to the given file. Returns false without mounting
	 * anything when the organization cannot host the local viewer, so callers can fall back to
	 * another display mechanism.
	 */
	async openFileViewer(fileId: string, options: { fieldId?: string; includeHidden?: boolean } = {}): Promise<boolean> {
		if (!this.form.canHostLocalFileViewer)
			return false;

		const token = ++this.fileViewerOpenToken;

		const manifestPromise = this.form.buildFileViewerManifest({ includeHidden: options.includeHidden === true });
		manifestPromise.catch(() => {});

		let manifest: EmbeddedFormFileViewerManifest;
		try {
			await this.ensureFileViewerMounted();
			manifest = await manifestPromise;
		}
		catch (e) {
			this.form.log && this.form.log.error(e as Error);
			return token !== this.fileViewerOpenToken;
		}

		if (token !== this.fileViewerOpenToken || !this.fileViewerLightboxComponent)
			return true;

		this.fileViewerLightboxComponent.manifest = manifest;
		this.selectFileViewerFile(fileId);
		this.fileViewerLightboxComponent.visible = true;

		return true;
	}

	closeFileViewer(): void {
		this.fileViewerOpenToken++;
		if (this.fileViewerLightboxComponent)
			this.fileViewerLightboxComponent.visible = false;
	}

	selectFileViewerFile(fileId: string): void {
		this.fileViewerCurrentFileId = fileId;
		if (this.fileViewerLightboxComponent)
			this.fileViewerLightboxComponent.selectedFileId = fileId || '';
	}

	/**
	 * Mounts the file viewer dock into `el`. `includeHidden` is captured for the lifetime of the
	 * mount and reused by every subsequent `refreshFileViewerDock` call; it defaults to excluded,
	 * and only a caller that explicitly opts in (e.g. the entries page) sees hidden fields.
	 */
	async mountFileViewerDock(el: HTMLElement, options: { includeHidden?: boolean } = {}): Promise<void> {
		if (!el || !this.form.canHostLocalFileViewer)
			return;

		this.unmountFileViewerDock();
		const generation = this.fileViewerDockMountGeneration;
		this.fileViewerDockIncludeHidden = options.includeHidden === true;

		const FileViewerDock = (await importWithRetry(() => import(/* webpackChunkName: "FileViewerDock" */ './FileViewerDock.vue'))).default;

		if (generation !== this.fileViewerDockMountGeneration)
			return;

		const component = this.createFileViewerHost(FileViewerDock, {
			manifest: emptyFileViewerManifest(),
			staticLibrariesUrl: this.form.session.staticLibrariesUrl || '',
			formClassName: this.form.$el.className
		}, {
			select: (file: FileViewerFile) => this.selectFileViewerDockFile(file.fileId)
		});

		component.$mount();
		el.appendChild(component.$el);

		this.fileViewerDockComponent = component;

		await this.refreshFileViewerDock();
	}

	unmountFileViewerLightbox(): void {
		this.fileViewerOpenToken++;
		this.fileViewerLightboxMountGeneration++;
		this.fileViewerLightboxMountPromise = null;
		if (this.fileViewerLightboxComponent) {
			try {
				this.fileViewerLightboxComponent.$destroy();
			}
			catch (e) {
			}
		}

		this.fileViewerLightboxComponent = null;
	}

	unmountFileViewerDock(): void {
		this.fileViewerDockMountGeneration++;
		if (this.fileViewerDockComponent) {
			try {
				this.fileViewerDockComponent.$destroy();
			}
			catch (e) {
			}

			if (this.fileViewerDockComponent.$el && this.fileViewerDockComponent.$el.parentNode)
				this.fileViewerDockComponent.$el.parentNode.removeChild(this.fileViewerDockComponent.$el);
		}

		this.fileViewerDockComponent = null;
	}

	/**
	 * Rebuilds and applies the dock's manifest. Idempotent and last-write-wins: two overlapping
	 * calls can resolve in either order, and only the manifest from the call issued last is ever
	 * applied.
	 */
	async refreshFileViewerDock(): Promise<void> {
		if (!this.fileViewerDockComponent)
			return;

		const token = ++this.fileViewerDockRefreshToken;
		const manifest = await this.form.buildFileViewerManifest({ includeHidden: this.fileViewerDockIncludeHidden });

		if (token !== this.fileViewerDockRefreshToken || !this.fileViewerDockComponent)
			return;

		this.fileViewerDockComponent.manifest = manifest;
	}

	selectFileViewerDockFile(fileId: string): void {
		this.fileViewerDockCurrentFileId = fileId;
	}
}
